import { Controller } from "@hotwired/stimulus"
import { start, move, zoomTo, cut, placed } from "../lib/avatar_frame.js"

// The avatar maker on the account page (accounts/_avatar_maker): a photo dragged and zoomed to fill the circle, cut
// out here and sent as a small square, or their letters on a colour. The photo never leaves the browser whole.
const SIDE = 512
// A big photo is drawn from a copy no wider than this, so dragging stays smooth.
const WORKING = 2048
const NUDGE = 10

export default class extends Controller {
  static targets = ["dialog", "stage", "canvas", "ring", "now", "letters", "file", "zoom", "zoomRow", "hint", "error",
    "save", "photoForm", "lettersForm", "lettersField"]
  static values = { letters: String }

  connect() {
    this.pointers = new Map()
  }

  open() {
    this.dialogTarget.showModal()
  }

  backdrop(event) {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  mode(event) {
    const letters = event.target.value === "letters"
    this.photoFormTarget.hidden = letters
    this.lettersFormTarget.hidden = !letters
    this.show(letters ? "letters" : this.frame ? "canvas" : "now")
  }

  // What the stage shows: the picture now (or the way to pick one), the photo being framed, or the letters.
  show(what) {
    this.nowTarget.hidden = what !== "now"
    this.canvasTarget.hidden = this.ringTarget.hidden = what !== "canvas"
    this.lettersTarget.hidden = what !== "letters"
    this.stageTarget.classList.toggle("is-framing", what === "canvas")
    if (what === "canvas") this.stageTarget.tabIndex = 0
    else this.stageTarget.removeAttribute("tabindex")
  }

  // The stage before a photo is picked opens the picker.
  choose() {
    if (!this.frame && !this.photoFormTarget.hidden) this.fileTarget.click()
  }

  async pick() {
    const file = this.fileTarget.files[0]
    if (!file) return
    this.errorTarget.hidden = true
    const url = URL.createObjectURL(file)
    try {
      const img = new Image()
      img.src = url
      await img.decode()
      this.source = this.working(img)
    } catch {
      this.refuse("That photo couldn't be opened. Try a JPG or PNG.")
      return
    } finally {
      URL.revokeObjectURL(url)
    }
    this.show("canvas")
    this.zoomRowTarget.hidden = this.hintTarget.hidden = false
    this.saveTarget.disabled = false
    this.set(start(this.source.width, this.source.height, this.ringTarget.offsetWidth))
    this.stageTarget.focus({ preventScroll: true })
  }

  // A copy of the photo no bigger than WORKING a side, turned the way it was taken.
  working(img) {
    const w = img.naturalWidth
    const h = img.naturalHeight
    if (!w || !h) throw new Error("empty")
    const s = Math.min(1, WORKING / Math.max(w, h))
    const copy = document.createElement("canvas")
    copy.width = Math.round(w * s)
    copy.height = Math.round(h * s)
    copy.getContext("2d").drawImage(img, 0, 0, copy.width, copy.height)
    return copy
  }

  refuse(line) {
    this.errorTarget.textContent = line
    this.errorTarget.hidden = false
    this.saveTarget.disabled = true
  }

  draw() {
    const size = this.stageTarget.offsetWidth
    const dpr = window.devicePixelRatio || 1
    const canvas = this.canvasTarget
    if (canvas.width !== Math.round(size * dpr)) canvas.width = canvas.height = Math.round(size * dpr)
    const ctx = canvas.getContext("2d")
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, size, size)
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(this.source, ...placed(this.frame, size))
  }

  set(frame) {
    this.frame = frame
    this.zoomTarget.value = frame.zoom
    this.draw()
  }

  zoom() {
    if (this.frame) this.set(zoomTo(this.frame, Number(this.zoomTarget.value)))
  }

  grab(event) {
    if (!this.frame) return
    this.stageTarget.setPointerCapture(event.pointerId)
    this.pointers.set(event.pointerId, [event.clientX, event.clientY])
    this.stageTarget.classList.add("is-dragging")
  }

  // One finger or the mouse moves the photo; two pinch it bigger or smaller.
  drag(event) {
    const was = this.pointers.get(event.pointerId)
    if (!was || !this.frame) return
    const spread = this.spread()
    this.pointers.set(event.pointerId, [event.clientX, event.clientY])
    if (this.pointers.size > 1) {
      this.set(zoomTo(this.frame, this.frame.zoom * (this.spread() / spread)))
    } else {
      this.set(move(this.frame, event.clientX - was[0], event.clientY - was[1]))
    }
  }

  spread() {
    const [a, b] = [...this.pointers.values()]
    return b ? Math.hypot(a[0] - b[0], a[1] - b[1]) || 1 : 1
  }

  drop(event) {
    this.pointers.delete(event.pointerId)
    if (this.pointers.size === 0) this.stageTarget.classList.remove("is-dragging")
  }

  wheel(event) {
    if (!this.frame) return
    event.preventDefault()
    this.set(zoomTo(this.frame, this.frame.zoom * Math.exp(-event.deltaY * 0.002)))
  }

  nudge(event) {
    if (!this.frame) return
    const step = { ArrowLeft: [NUDGE, 0], ArrowRight: [-NUDGE, 0], ArrowUp: [0, NUDGE], ArrowDown: [0, -NUDGE] }[event.key]
    const zoom = { "+": 1.1, "=": 1.1, "-": 1 / 1.1 }[event.key]
    if (!step && !zoom) return
    event.preventDefault()
    this.set(step ? move(this.frame, ...step) : zoomTo(this.frame, this.frame.zoom * zoom))
  }

  // The circle cut out at SIDE pixels, on white where the photo is clear, sent in place of the photo picked.
  send(event) {
    if (!this.frame || this.sending) return
    event.preventDefault()
    const out = document.createElement("canvas")
    out.width = out.height = SIDE
    const ctx = out.getContext("2d")
    ctx.fillStyle = "#fff"
    ctx.fillRect(0, 0, SIDE, SIDE)
    ctx.imageSmoothingQuality = "high"
    const [x, y, side] = cut(this.frame)
    ctx.drawImage(this.source, x, y, side, side, 0, 0, SIDE, SIDE)
    this.saveTarget.disabled = true
    this.sending = true
    out.toBlob((blob) => {
      try {
        const files = new DataTransfer()
        files.items.add(new File([blob], "photo.jpg", { type: "image/jpeg" }))
        this.fileTarget.files = files.files
      } catch {
        // Without a way to swap the file, the photo goes as it was, and is cut to its middle when it's kept.
      }
      this.photoFormTarget.submit()
    }, "image/jpeg", 0.9)
  }

  // The letters as they'll be kept, on the colour picked.
  preview() {
    const typed = this.lettersFieldTarget.value.replace(/[^\p{L}\p{N}]/gu, "").slice(0, 2).toUpperCase()
    const shown = this.lettersTarget.firstElementChild
    shown.textContent = typed || this.lettersValue
    const color = this.lettersFormTarget.querySelector("input[type=radio]:checked")?.value || "indigo"
    shown.className = `acct-avatar av-big av-${color}`
  }
}
